import { useState, useEffect, useMemo, useRef } from "react"
import {
  X,
  Search,
  Trash2,
  CalendarDays,
  AlertCircle,
  Loader2,
} from "lucide-react"
import { useSelectableUsers } from "../hooks/useEquipmentNotify"
import {
  SCHEDULE_TYPE_LIST,
  SCHEDULE_TYPE_META,
  type ScheduleType,
  type ScheduleAttendee,
  type TeamSchedule,
  type ScheduleInput,
  diffDays,
  toDateKey,
} from "../hooks/useTeamSchedules"

interface ScheduleEventModalProps {
  /** 수정 모드일 때 기존 일정 */
  schedule?: TeamSchedule | null
  /** 신규 등록 시 기본 시작일 (YYYY-MM-DD) */
  defaultDate?: string
  /** 신규 등록 시 기본 대상자 (보통 본인) */
  defaultAttendees?: ScheduleAttendee[]
  currentUserId?: string
  currentUserName?: string
  currentUserEmail?: string
  /** 수정·삭제 권한 여부 */
  canEdit?: boolean
  onClose: () => void
  onSubmit: (input: ScheduleInput) => Promise<void>
  onDelete?: () => Promise<void>
}

export function ScheduleEventModal({
  schedule,
  defaultDate,
  defaultAttendees = [],
  currentUserId,
  currentUserName,
  currentUserEmail,
  canEdit = true,
  onClose,
  onSubmit,
  onDelete,
}: ScheduleEventModalProps) {
  const isEdit = Boolean(schedule)
  const { users, loaded } = useSelectableUsers()

  const [scheduleType, setScheduleType] = useState<ScheduleType>(
    schedule?.scheduleType ?? "vacation_full",
  )
  const [title, setTitle] = useState(schedule?.title ?? "")
  const [description, setDescription] = useState(schedule?.description ?? "")
  const [startDate, setStartDate] = useState(
    schedule?.startDate ?? defaultDate ?? toDateKey(new Date()),
  )
  const [endDate, setEndDate] = useState(
    schedule?.endDate ?? defaultDate ?? toDateKey(new Date()),
  )
  const [attendees, setAttendees] = useState<ScheduleAttendee[]>(
    schedule?.attendees ?? defaultAttendees,
  )
  const [userSearch, setUserSearch] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [inlineError, setInlineError] = useState("")

  // 오버레이 드래그로 닫히지 않도록 mousedown 시작 지점을 기록
  const overlayMouseDown = useRef(false)

  const handleOverlayMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    overlayMouseDown.current = e.target === e.currentTarget
  }
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && overlayMouseDown.current) {
      onClose()
    }
    overlayMouseDown.current = false
  }

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [onClose])

  const selectedIds = useMemo(
    () => new Set(attendees.map((a) => a.userId)),
    [attendees],
  )

  const filteredUsers = useMemo(() => {
    const keyword = userSearch.trim().toLowerCase()
    if (!keyword) return users
    return users.filter((u) => {
      const haystack = `${u.userName} ${u.teamName ?? ""} ${u.email}`.toLowerCase()
      return haystack.includes(keyword)
    })
  }, [users, userSearch])

  const addAttendee = (userId: string) => {
    if (selectedIds.has(userId)) return
    const found = users.find((u) => u.userId === userId)
    if (!found) return
    setAttendees((prev) => [
      ...prev,
      {
        userId: found.userId,
        userName: found.userName,
        teamName: found.teamName ?? "",
        email: found.email ?? "",
      },
    ])
    setInlineError("")
  }

  const removeAttendee = (userId: string) => {
    setAttendees((prev) => prev.filter((a) => a.userId !== userId))
  }

  // 기간 유효성
  const spanDays = useMemo(() => diffDays(startDate, endDate), [startDate, endDate])
  const periodInvalid = spanDays < 0
  const periodTooLong = spanDays > 30 // 당일 포함 최대 31일

  const canSubmit =
    scheduleType.length > 0 &&
    attendees.length > 0 &&
    !periodInvalid &&
    !periodTooLong &&
    !submitting &&
    !deleting

  const handleStartDateChange = (value: string) => {
    setStartDate(value)
    // 시작일이 종료일보다 뒤면 종료일도 함께 이동
    if (value && diffDays(value, endDate) < 0) setEndDate(value)
  }

  const handleSubmit = async () => {
    if (!canSubmit) return
    if (attendees.length === 0) {
      setInlineError("대상자를 1명 이상 선택해주세요.")
      return
    }
    setSubmitting(true)
    setInlineError("")
    try {
      await onSubmit({
        scheduleType,
        title: title.trim(),
        description: description.trim(),
        startDate,
        endDate,
        attendees,
        creatorName: currentUserName,
        creatorEmail: currentUserEmail,
      })
    } catch (e) {
      console.error("일정 저장 실패:", e)
      setInlineError("일정을 저장하지 못했습니다. 다시 시도해주세요.")
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!onDelete) return
    setDeleting(true)
    try {
      await onDelete()
    } catch (e) {
      console.error("일정 삭제 실패:", e)
      setInlineError("일정을 삭제하지 못했습니다. 다시 시도해주세요.")
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3 sm:p-6"
      onMouseDown={handleOverlayMouseDown}
      onClick={handleOverlayClick}
    >
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* 헤더 */}
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-[#F59E0B]" />
            <h3 className="text-base font-bold text-gray-900 sm:text-lg">
              {isEdit ? "일정 수정" : "일정 등록"}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
            aria-label="닫기"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* 본문 */}
        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
          {/* 일정 유형 */}
          <div>
            <label className="mb-2 block text-sm font-semibold text-gray-800">
              일정 유형 <span className="text-rose-500">*</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {SCHEDULE_TYPE_LIST.map((meta) => {
                const selected = scheduleType === meta.key
                return (
                  <button
                    key={meta.key}
                    type="button"
                    onClick={() => setScheduleType(meta.key)}
                    className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition sm:text-sm ${
                      selected
                        ? `${meta.chip} ring-2 ring-[#F59E0B] ring-offset-1`
                        : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    {meta.label}
                  </button>
                )
              })}
            </div>
          </div>

          {/* 기간 */}
          <div>
            <label className="mb-2 block text-sm font-semibold text-gray-800">
              기간 <span className="text-rose-500">*</span>
            </label>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <input
                type="date"
                value={startDate}
                onChange={(e) => handleStartDateChange(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#F59E0B] focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20"
              />
              <span className="hidden text-gray-400 sm:block">~</span>
              <input
                type="date"
                value={endDate}
                min={startDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#F59E0B] focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20"
              />
            </div>
            {periodInvalid ? (
              <p className="mt-1.5 flex items-center gap-1 text-xs font-medium text-rose-600">
                <AlertCircle className="h-3.5 w-3.5" />
                종료일은 시작일과 같거나 이후여야 합니다.
              </p>
            ) : periodTooLong ? (
              <p className="mt-1.5 flex items-center gap-1 text-xs font-medium text-rose-600">
                <AlertCircle className="h-3.5 w-3.5" />
                한 일정은 최대 31일까지 등록할 수 있습니다.
              </p>
            ) : (
              <p className="mt-1.5 text-xs text-gray-500">
                {startDate === endDate
                  ? "당일 일정입니다."
                  : `${spanDays + 1}일 일정입니다.`}
              </p>
            )}
          </div>

          {/* 대상 사용자 */}
          <div>
            <label className="mb-2 block text-sm font-semibold text-gray-800">
              대상 사용자 <span className="text-rose-500">*</span>
              <span className="ml-1 text-xs font-normal text-gray-500">
                ({attendees.length}명 선택)
              </span>
            </label>

            {/* 선택된 칩 */}
            {attendees.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1.5">
                {attendees.map((a) => (
                  <span
                    key={a.userId}
                    className="inline-flex items-center gap-1 rounded-full border border-orange-200 bg-orange-50 px-2.5 py-1 text-xs font-medium text-orange-700"
                  >
                    {a.userName}
                    {a.teamName ? (
                      <span className="text-[10px] text-orange-500">
                        {a.teamName}
                      </span>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => removeAttendee(a.userId)}
                      className="ml-0.5 rounded-full p-0.5 transition hover:bg-orange-200"
                      aria-label={`${a.userName} 제거`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* 검색 */}
            <div className="relative mb-2">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="이름 또는 팀으로 검색"
                className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-9 text-sm focus:border-[#F59E0B] focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20"
              />
              {userSearch && (
                <button
                  type="button"
                  onClick={() => setUserSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:bg-gray-100"
                  aria-label="검색어 지우기"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {/* 후보 목록 */}
            <div className="max-h-44 overflow-y-auto rounded-lg border border-gray-200">
              {!loaded ? (
                <div className="flex items-center justify-center gap-2 py-6 text-sm text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  사용자 목록 불러오는 중...
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="py-6 text-center text-sm text-gray-500">
                  {userSearch
                    ? `'${userSearch}'와 일치하는 사용자가 없습니다.`
                    : "선택 가능한 사용자가 없습니다."}
                </div>
              ) : (
                filteredUsers.map((u) => {
                  const selected = selectedIds.has(u.userId)
                  return (
                    <button
                      key={u.userId}
                      type="button"
                      onClick={() => addAttendee(u.userId)}
                      disabled={selected}
                      className={`flex w-full items-center justify-between border-b border-gray-100 px-3 py-2 text-left text-sm transition last:border-b-0 ${
                        selected
                          ? "cursor-default bg-gray-50 text-gray-400"
                          : "text-gray-700 hover:bg-orange-50"
                      }`}
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="truncate font-medium">{u.userName}</span>
                        {u.teamName ? (
                          <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500">
                            {u.teamName}
                          </span>
                        ) : null}
                      </span>
                      {selected ? (
                        <span className="shrink-0 text-[11px] font-medium text-[#F59E0B]">
                          선택됨
                        </span>
                      ) : (
                        <span className="shrink-0 text-[11px] text-gray-400">
                          추가
                        </span>
                      )}
                    </button>
                  )
                })
              )}
            </div>
          </div>

          {/* 제목 */}
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-gray-800">
              제목 <span className="text-xs font-normal text-gray-400">(선택)</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, 60))}
              placeholder={`비워두면 '${SCHEDULE_TYPE_META[scheduleType].label}'으로 표시됩니다.`}
              maxLength={60}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#F59E0B] focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20"
            />
            <p className="mt-1 text-right text-[11px] text-gray-400">
              {title.length} / 60
            </p>
          </div>

          {/* 내용 */}
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-gray-800">
              내용 <span className="text-xs font-normal text-gray-400">(선택)</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, 200))}
              placeholder="일정에 대한 상세 내용을 입력하세요."
              rows={3}
              maxLength={200}
              className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#F59E0B] focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/20"
            />
            <p className="mt-1 text-right text-[11px] text-gray-400">
              {description.length} / 200
            </p>
          </div>

          {inlineError && (
            <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{inlineError}</span>
            </div>
          )}
        </div>

        {/* 푸터 */}
        <div className="flex items-center justify-between gap-2 border-t border-gray-200 px-5 py-3">
          <div>
            {isEdit && canEdit && onDelete && (
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                disabled={deleting || submitting}
                className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 px-3 py-2 text-xs font-semibold text-rose-600 transition hover:bg-rose-50 disabled:opacity-50 sm:text-sm"
              >
                <Trash2 className="h-4 w-4" />
                삭제
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting || deleting}
              className="rounded-lg border border-gray-300 px-4 py-2 text-xs font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 sm:text-sm"
            >
              취소
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#F59E0B] px-4 py-2 text-xs font-semibold text-white transition hover:bg-[#D97706] disabled:cursor-not-allowed disabled:opacity-50 sm:text-sm"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {submitting ? "저장 중..." : isEdit ? "수정" : "저장"}
            </button>
          </div>
        </div>
      </div>

      {/* 삭제 확인 */}
      {confirmDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-2xl">
            <h4 className="mb-2 text-base font-bold text-gray-900">
              일정을 삭제하시겠습니까?
            </h4>
            <p className="mb-4 text-sm text-gray-600">
              삭제한 일정은 복구할 수 없습니다.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
                className="rounded-lg border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 sm:text-sm"
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="inline-flex items-center gap-1.5 rounded-lg bg-rose-600 px-3 py-2 text-xs font-semibold text-white hover:bg-rose-700 disabled:opacity-50 sm:text-sm"
              >
                {deleting && <Loader2 className="h-4 w-4 animate-spin" />}
                {deleting ? "삭제 중..." : "삭제"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
